import { useState } from "react";
import { Mail, Phone, MapPin, ArrowRight, Check, Instagram } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1600607687644-c7171b42498f?auto=format&fit=crop&w=2000&q=80";

const projectTypes = [
  "Corporate Design",
  "Residential Design",
  "Restaurant & Café",
  "Turnkey Solution",
  "Custom Furniture",
  "Other",
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Message sent successfully! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        project_type: "",
        message: "",
      });
    } catch {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-end pb-20" data-testid="contact-hero">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt="Contact Us"
            className="w-full h-full object-cover"
          />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="container-custom relative z-10 pt-32">
          <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body opacity-0 animate-fade-in">
            Get in Touch
          </p>
          <h1 className="font-heading text-5xl md:text-7xl text-black opacity-0 animate-fade-in animate-delay-100">
            Let's Start
            <br />
            <span className="italic text-warmBrown">Your Project</span>
          </h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding bg-cream" data-testid="contact-content">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Info */}
            <div>
              <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-4 font-body">
                Contact Information
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-black mb-8">
                We'd Love to
                <br />
                <span className="italic text-warmBrown">Hear From You</span>
              </h2>
              <p className="text-black/60 font-body leading-relaxed mb-12">
                Ready to transform your space? Whether you have a clear vision
                or just the beginnings of an idea, we're here to help bring it
                to life. Get in touch to start the conversation.
              </p>

              {/* Team Contact Cards */}
              <div className="space-y-6">
                {/* Nadine Harris */}
                <div className="p-6 bg-soft-beige border border-black/5" data-testid="contact-card-nadine">
                  <h3 className="font-heading text-xl text-black mb-4">Nadine Harris</h3>
                  <div className="space-y-3">
                    <a
                      href="mailto:nadine@nhinteriors.co.za"
                      className="flex items-center gap-3 text-black/70 hover:text-warmBrown transition-colors duration-300 font-body text-sm"
                    >
                      <Mail size={16} className="text-warmBrown flex-shrink-0" />
                      nadine@nhinteriors.co.za
                    </a>
                    <a
                      href="tel:+27827823723"
                      className="flex items-center gap-3 text-black/70 hover:text-warmBrown transition-colors duration-300 font-body text-sm"
                    >
                      <Phone size={16} className="text-warmBrown flex-shrink-0" />
                      +27 82 782 3723
                    </a>
                  </div>
                </div>

                {/* Joshua De Sousa Coutinho */}
                <div className="p-6 bg-soft-beige border border-black/5" data-testid="contact-card-joshua">
                  <h3 className="font-heading text-xl text-black mb-4">Joshua De Sousa Coutinho</h3>
                  <div className="space-y-3">
                    <a
                      href="mailto:joshua@nhinteriors.co.za"
                      className="flex items-center gap-3 text-black/70 hover:text-warmBrown transition-colors duration-300 font-body text-sm"
                    >
                      <Mail size={16} className="text-warmBrown flex-shrink-0" />
                      joshua@nhinteriors.co.za
                    </a>
                    <a
                      href="tel:+27733835252"
                      className="flex items-center gap-3 text-black/70 hover:text-warmBrown transition-colors duration-300 font-body text-sm"
                    >
                      <Phone size={16} className="text-warmBrown flex-shrink-0" />
                      +27 73 383 5252
                    </a>
                  </div>
                </div>

                {/* Jessica Rose Burch */}
                <div className="p-6 bg-soft-beige border border-black/5" data-testid="contact-card-jessica">
                  <h3 className="font-heading text-xl text-black mb-4">Jessica Rose Burch</h3>
                  <div className="space-y-3">
                    <a
                      href="mailto:jessica@nhinteriors.co.za"
                      className="flex items-center gap-3 text-black/70 hover:text-warmBrown transition-colors duration-300 font-body text-sm"
                    >
                      <Mail size={16} className="text-warmBrown flex-shrink-0" />
                      jessica@nhinteriors.co.za
                    </a>
                    <a
                      href="tel:+27728438987"
                      className="flex items-center gap-3 text-black/70 hover:text-warmBrown transition-colors duration-300 font-body text-sm"
                    >
                      <Phone size={16} className="text-warmBrown flex-shrink-0" />
                      +27 72 843 8987
                    </a>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="mt-8 p-6 bg-soft-beige border border-black/5" data-testid="contact-location">
                <div className="flex items-start gap-4">
                  <MapPin size={18} className="text-warmBrown flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-black font-body font-medium">Cape Town, South Africa</p>
                    <p className="text-black/50 font-body text-sm mt-1">
                      Available for national & international projects
                    </p>
                  </div>
                </div>
              </div>

              {/* Working Hours */}
              <div className="mt-6 p-6 bg-soft-beige border border-black/5">
                <p className="text-black/40 text-xs uppercase tracking-wider mb-3 font-body">
                  Office Hours
                </p>
                <p className="text-black font-body">Monday – Friday: 9am – 5pm</p>
                <p className="text-black/50 font-body text-sm mt-2">
                  Consultations available by appointment
                </p>
              </div>

              {/* Instagram */}
              <a
                href="https://www.instagram.com/nh_interior_design_/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 p-6 bg-soft-beige border border-black/5 flex items-center gap-4 group hover:border-warmBrown/30 transition-colors duration-300"
                data-testid="contact-instagram"
              >
                <Instagram size={20} className="text-warmBrown flex-shrink-0" />
                <div>
                  <p className="text-black font-body font-medium group-hover:text-warmBrown transition-colors duration-300">@nh_interior_design_</p>
                  <p className="text-black/50 font-body text-sm mt-0.5">Follow us on Instagram</p>
                </div>
              </a>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 md:p-12 border border-black/5 shadow-lg">
              {isSubmitted ? (
                <div className="text-center py-12" data-testid="form-success">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-warmBrown/20 rounded-full mb-6">
                    <Check className="text-warmBrown" size={32} />
                  </div>
                  <h3 className="font-heading text-2xl text-black mb-4">
                    Message Sent!
                  </h3>
                  <p className="text-black/60 font-body mb-8">
                    Thank you for reaching out. We'll review your inquiry and
                    get back to you within 24-48 hours.
                  </p>
                  <button
                    onClick={() => setIsSubmitted(false)}
                    className="text-warmBrown hover:text-black transition-colors duration-300 font-body text-sm uppercase tracking-wider"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <p className="text-warmBrown uppercase tracking-[0.3em] text-xs mb-2 font-body">
                    Send a Message
                  </p>
                  <h3 className="font-heading text-2xl text-black mb-8">
                    Tell Us About Your Project
                  </h3>

                  <div className="space-y-6">
                    {/* Name */}
                    <div>
                      <label
                        htmlFor="name"
                        className="text-black/40 text-xs uppercase tracking-wider font-body block mb-2"
                      >
                        Your Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-black/20 py-4 text-black font-body focus:border-warmBrown transition-colors duration-300 outline-none"
                        placeholder="John Smith"
                        data-testid="input-name"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label
                        htmlFor="email"
                        className="text-black/40 text-xs uppercase tracking-wider font-body block mb-2"
                      >
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-b border-black/20 py-4 text-black font-body focus:border-warmBrown transition-colors duration-300 outline-none"
                        placeholder="john@example.com"
                        data-testid="input-email"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <label
                        htmlFor="phone"
                        className="text-black/40 text-xs uppercase tracking-wider font-body block mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full bg-transparent border-b border-black/20 py-4 text-black font-body focus:border-warmBrown transition-colors duration-300 outline-none"
                        placeholder="+27 82 000 0000"
                        data-testid="input-phone"
                      />
                    </div>

                    {/* Project Type */}
                    <div>
                      <label
                        htmlFor="project_type"
                        className="text-black/40 text-xs uppercase tracking-wider font-body block mb-2"
                      >
                        Project Type
                      </label>
                      <select
                        id="project_type"
                        name="project_type"
                        value={formData.project_type}
                        onChange={handleChange}
                        className="w-full bg-transparent border-b border-black/20 py-4 text-black font-body focus:border-warmBrown transition-colors duration-300 outline-none cursor-pointer"
                        data-testid="input-project-type"
                      >
                        <option value="" className="bg-white">
                          Select a project type
                        </option>
                        {projectTypes.map((type) => (
                          <option key={type} value={type} className="bg-white">
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label
                        htmlFor="message"
                        className="text-black/40 text-xs uppercase tracking-wider font-body block mb-2"
                      >
                        Your Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full bg-transparent border-b border-black/20 py-4 text-black font-body focus:border-warmBrown transition-colors duration-300 outline-none resize-none"
                        placeholder="Tell us about your project, timeline, and any specific requirements..."
                        data-testid="input-message"
                      />
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-primary flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed mt-8"
                      data-testid="submit-button"
                    >
                      {isSubmitting ? (
                        "Sending..."
                      ) : (
                        <>
                          Send Message
                          <ArrowRight size={16} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map/Location Section */}
      <section className="py-16 bg-soft-beige border-t border-black/5" data-testid="location-section">
        <div className="container-custom text-center">
          <p className="text-black/40 text-sm font-body mb-4">
            Based in the beautiful Mother City
          </p>
          <h3 className="font-heading text-3xl md:text-4xl text-black mb-4">
            Cape Town, South Africa
          </h3>
          <p className="text-black/60 font-body max-w-xl mx-auto">
            While we're based in Cape Town, we work with clients across South
            Africa and internationally. Distance is never a barrier to great
            design.
          </p>
        </div>
      </section>
    </div>
  );
}
